import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
} from 'firebase/firestore';
import { db, storage } from '../../firebase';
import {
  BanknotesIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  UserPlusIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  UserCircleIcon,
  CheckCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import PrequalFinancialMetricsDisplay from '../admin/PrequalFinancialMetricsDisplay';
import PrequalificationTable from '../admin/PrequalificationTable';
import { generatePrequalSummaryPDF } from '../admin/PrequalSummaryPdfGenerator';
import BusinessIntakeDocuments from './BusinessIntakeDocuments';

const PrequalificationDetailsView = ({ application, onBack, customLogo }) => {
  const [prequalData, setPrequalData] = useState(null);
  const [ocrData, setOcrData] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isFinancialOverviewExpanded, setIsFinancialOverviewExpanded] = useState(true);
  const [isUploadedFilesExpanded, setIsUploadedFilesExpanded] = useState(true);
  const [isBusinessDetailsExpanded, setIsBusinessDetailsExpanded] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isBorrowersExpanded, setIsBorrowersExpanded] = useState(true);
  const [borrowers, setBorrowers] = useState([]);
  const [linkCopied, setLinkCopied] = useState(false);
  const [chosenBuyer, setChosenBuyer] = useState(null);
  const [isSelectingBuyer, setIsSelectingBuyer] = useState(false);

  // Generate the signup link for this prequalification
  const signupLink = application?.id
    ? `${window.location.origin}/prequal-signup/${application.id}`
    : '';

  const copySignupLink = () => {
    navigator.clipboard.writeText(signupLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Fetch borrowers who signed up through this prequalification
  useEffect(() => {
    const fetchBorrowers = async () => {
      if (!application?.id) return;

      try {
        // Get borrowers from the prequalification document
        const prequalDocRef = doc(db, 'prequalificationApplications', application.id);
        const prequalDocSnap = await getDoc(prequalDocRef);

        if (prequalDocSnap.exists()) {
          const data = prequalDocSnap.data();
          if (data.borrowers && Array.isArray(data.borrowers)) {
            setBorrowers(data.borrowers);
          }
          // Also get the chosen buyer if one exists
          if (data.chosenBuyer) {
            setChosenBuyer(data.chosenBuyer);
          }
        }
      } catch (error) {
        console.error('Error fetching borrowers:', error);
      }
    };

    fetchBorrowers();
  }, [application]);

  // Function to select a borrower as the chosen buyer
  const handleChooseBuyer = async (borrower) => {
    if (!application?.id) return;

    const confirmSelection = window.confirm(
      `Are you sure you want to select ${borrower.firstName} ${borrower.lastName} as the buyer for this listing?`
    );

    if (!confirmSelection) return;

    setIsSelectingBuyer(true);

    try {
      const prequalDocRef = doc(db, 'prequalificationApplications', application.id);

      // Build chosenBuyer object, only including defined fields
      const chosenBuyerData = {
        firstName: borrower.firstName || '',
        lastName: borrower.lastName || '',
        email: borrower.email || '',
        selectedAt: new Date().toISOString()
      };

      // Add id if it exists (could be id, odeddiudi, or odeddiudi)
      if (borrower.id) chosenBuyerData.id = borrower.id;
      if (borrower.odeddiudi) chosenBuyerData.odeddiudi = borrower.odeddiudi;
      if (borrower.loanApplicationId) chosenBuyerData.loanApplicationId = borrower.loanApplicationId;

      await updateDoc(prequalDocRef, {
        chosenBuyer: chosenBuyerData,
        status: 'buyer_selected',
        buyerSelectedAt: new Date().toISOString()
      });

      // Also update the borrower's loanApplication to mark their business as chosen
      // Try by loanApplicationId first, then by email
      let loanAppRef = null;
      let loanData = null;

      if (borrower.loanApplicationId) {
        loanAppRef = doc(db, 'loanApplications', borrower.loanApplicationId);
        const loanAppSnap = await getDoc(loanAppRef);
        if (loanAppSnap.exists()) {
          loanData = loanAppSnap.data();
        }
      }

      // If no loanApplicationId or it doesn't exist, search by email
      if (!loanData && borrower.email) {
        const loansRef = collection(db, 'loanApplications');
        const emailQuery = query(loansRef, where('email', '==', borrower.email));
        const querySnapshot = await getDocs(emailQuery);

        if (!querySnapshot.empty) {
          const loanDoc = querySnapshot.docs[0];
          loanAppRef = doc(db, 'loanApplications', loanDoc.id);
          loanData = loanDoc.data();
          // Save the loanApplicationId for later use
          chosenBuyerData.loanApplicationId = loanDoc.id;
        }
      }

      if (loanData && loanAppRef) {
        const businesses = loanData.businesses || [];

        // Find the business that matches this prequalification and mark it as chosen
        const updatedBusinesses = businesses.map(business => {
          if (business.prequalificationId === application.id) {
            return {
              ...business,
              chosenAsBuyer: true,
              chosenAt: new Date().toISOString(),
              listingName: application.businessName || 'Prequalified Listing'
            };
          }
          return business;
        });

        // Promote this loan application to a "full deal" under the listing's broker
        // so it shows up in the broker portal's Full Deals section.
        const brokerIdForDeal = application.brokerId || prequalData?.brokerId;
        const loanAppUpdates = {
          businesses: updatedBusinesses,
          hasChosenListing: true,
          source: 'broker_deal',
          prequalificationId: application.id,
          listingName: application.businessName || loanData.businessName || null,
          convertedToFullDealAt: serverTimestamp(),
        };
        if (brokerIdForDeal) {
          loanAppUpdates.brokerId = brokerIdForDeal;
        }
        // Default a status if none is set so the deal appears as "in progress"
        if (!loanData.loanStatus) {
          loanAppUpdates.loanStatus = 'In progress';
        }
        // Carry over key deal economics from the prequalification when missing
        if (application.businessName && !loanData.businessName) {
          loanAppUpdates.businessName = application.businessName;
        }
        if (application.projectedSalePrice && !loanData.purchasePrice) {
          loanAppUpdates.purchasePrice = application.projectedSalePrice;
        }

        await updateDoc(loanAppRef, loanAppUpdates);

        // Register the deal on the broker's record so the portal picks it up
        if (brokerIdForDeal) {
          try {
            const brokerRef = doc(db, 'brokers', brokerIdForDeal);
            await updateDoc(brokerRef, {
              loanApplications: arrayUnion(loanAppRef.id),
            });
          } catch (brokerUpdateErr) {
            console.error('Could not append loanApplication to broker.loanApplications:', brokerUpdateErr);
          }
        }

        // If we found loanApplicationId via email search, update the prequalification doc with it
        if (chosenBuyerData.loanApplicationId && !borrower.loanApplicationId) {
          await updateDoc(prequalDocRef, {
            'chosenBuyer.loanApplicationId': chosenBuyerData.loanApplicationId
          });
        }
      }

      setChosenBuyer(chosenBuyerData);

      alert(`${borrower.firstName} ${borrower.lastName} has been selected as the buyer!`);
    } catch (error) {
      console.error('Error selecting buyer:', error);
      alert('Failed to select buyer. Please try again.');
    } finally {
      setIsSelectingBuyer(false);
    }
  };

  // Function to remove chosen buyer
  const handleRemoveChosenBuyer = async () => {
    if (!application?.id || !chosenBuyer) return;

    const confirmRemoval = window.confirm(
      'Are you sure you want to remove the selected buyer?'
    );

    if (!confirmRemoval) return;

    try {
      const prequalDocRef = doc(db, 'prequalificationApplications', application.id);

      // Find the loan application - try by ID first, then by email
      let loanAppRef = null;
      let loanData = null;

      if (chosenBuyer.loanApplicationId) {
        loanAppRef = doc(db, 'loanApplications', chosenBuyer.loanApplicationId);
        const loanAppSnap = await getDoc(loanAppRef);
        if (loanAppSnap.exists()) {
          loanData = loanAppSnap.data();
        }
      }

      // If no loanApplicationId or it doesn't exist, search by email
      if (!loanData && chosenBuyer.email) {
        const loansRef = collection(db, 'loanApplications');
        const emailQuery = query(loansRef, where('email', '==', chosenBuyer.email));
        const querySnapshot = await getDocs(emailQuery);

        if (!querySnapshot.empty) {
          const loanDoc = querySnapshot.docs[0];
          loanAppRef = doc(db, 'loanApplications', loanDoc.id);
          loanData = loanDoc.data();
        }
      }

      // Update the loan application if found
      if (loanData && loanAppRef) {
        const businesses = loanData.businesses || [];

        // Remove chosenAsBuyer from matching business
        const updatedBusinesses = businesses.map(business => {
          if (business.prequalificationId === application.id) {
            const { chosenAsBuyer, chosenAt, listingName, ...rest } = business;
            return rest;
          }
          return business;
        });

        // Check if any other businesses are still chosen
        const stillHasChosen = updatedBusinesses.some(b => b.chosenAsBuyer);

        const updates = {
          businesses: updatedBusinesses,
          hasChosenListing: stillHasChosen,
        };
        // If no other listing on this loan app is still chosen, demote it back
        // out of the broker's Full Deals view.
        if (!stillHasChosen && loanData.source === 'broker_deal' && loanData.prequalificationId === application.id) {
          updates.source = null;
          updates.prequalificationId = null;
          updates.convertedToFullDealAt = null;
        }
        await updateDoc(loanAppRef, updates);
      }

      await updateDoc(prequalDocRef, {
        chosenBuyer: null,
        status: 'prequalified',
        buyerSelectedAt: null
      });

      setChosenBuyer(null);
      alert('Buyer selection has been removed.');
    } catch (error) {
      console.error('Error removing buyer:', error);
      alert('Failed to remove buyer. Please try again.');
    }
  };

  useEffect(() => {
    const fetchPrequalData = async () => {
      if (!application || !application.id) return;
      
      try {
        // Get the full prequalification data
        const prequalDocRef = doc(db, 'prequalificationApplications', application.id);
        const prequalDocSnap = await getDoc(prequalDocRef);
        
        if (prequalDocSnap.exists()) {
          const data = { id: prequalDocSnap.id, ...prequalDocSnap.data() };
          console.log("Fetched prequalData:", data);

          // Fetch broker name if brokerId exists
          if (data.brokerId && !data.brokerName) {
            try {
              const brokerDocRef = doc(db, 'brokers', data.brokerId);
              const brokerDocSnap = await getDoc(brokerDocRef);
              if (brokerDocSnap.exists()) {
                const brokerData = brokerDocSnap.data();
                const fullName = brokerData.firstName && brokerData.lastName
                  ? `${brokerData.firstName} ${brokerData.lastName}`
                  : brokerData.companyName || brokerData.name || brokerData.email || null;
                data.brokerName = fullName;
              }
            } catch (brokerErr) {
              console.error('Error fetching broker:', brokerErr);
            }
          }

          setPrequalData(data);

          // Set uploaded files
          if (data.documents && Array.isArray(data.documents)) {
            setUploadedFiles(data.documents);
          } else if (data.documentMetadata && Array.isArray(data.documentMetadata)) {
            setUploadedFiles(data.documentMetadata);
          } else {
            setUploadedFiles([]);
          }
        }
      } catch (error) {
        console.error("Error fetching prequalification data:", error);
      }
    };

    const fetchOCRData = async () => {
      if (!application || !application.id) return;
      
      try {
        // Query OCRMetadata collection for this application
        const ocrMetadataRef = collection(db, 'OCRMetadata');
        const q = query(ocrMetadataRef, where('applicationId', '==', application.id));
        const querySnapshot = await getDocs(q);
        
        const processedData = {};
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.documentName === "Business Tax Returns") {
            // Use improved regex to extract year from labels like "2023 Business Tax Return"
            // Check if label exists before trying to match
            let year = null;
            if (data.label) {
              const yearMatch = data.label.match(/\b(20\d{2})\b/);
              year = yearMatch ? yearMatch[1] : data.label;
              console.log(`Extracted year ${year} from label: "${data.label}"`);
            } else if (data.fileName) {
              // Try extracting from fileName if label is missing
              const yearMatch = data.fileName.match(/\b(20\d{2})\b/);
              year = yearMatch ? yearMatch[1] : 'Unknown';
              console.log(`Extracted year ${year} from fileName: "${data.fileName}"`);
            }
            if (year && data.ocrResult) {
              processedData[year] = data.ocrResult;
            }
          }
        });
        
        // If no data found in OCRMetadata, try the ocrData collection
        if (Object.keys(processedData).length === 0) {
          const ocrDocRef = doc(db, 'ocrData', application.id);
          const ocrDocSnap = await getDoc(ocrDocRef);
          
          if (ocrDocSnap.exists()) {
            setOcrData(ocrDocSnap.data());
          }
        } else {
          setOcrData(processedData);
        }
        
        console.log("Fetched OCR Data:", processedData);
      } catch (error) {
        console.error("Error fetching OCR data:", error);
      }
    };

    fetchPrequalData();
    fetchOCRData();
  }, [application]);

  const toggleFinancialOverview = () => setIsFinancialOverviewExpanded(!isFinancialOverviewExpanded);
  const toggleUploadedFiles = () => setIsUploadedFilesExpanded(!isUploadedFilesExpanded);
  const toggleBusinessDetails = () => setIsBusinessDetailsExpanded(!isBusinessDetailsExpanded);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD', 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    }).format(value);
  };

  const handleGenerateSummaryPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      // Prepare data for PDF generation
      const pdfData = {
        businessName: prequalData?.businessName || application?.businessName || 'N/A',
        prequalifiedAmount: prequalData?.prequalifiedAmount || application?.prequalifiedAmount || 0,
        prequalificationType: prequalData?.prequalificationType || application?.prequalificationType || 'Adjusted Purchase Price',
        prequalificationEquityPercent: prequalData?.prequalificationEquityPercent || application?.prequalificationEquityPercent || 10,
        brokerName: prequalData?.brokerName || application?.brokerName || null,
        realEstateValue: prequalData?.realEstateValue || application?.realEstateValue || prequalData?.realEstateAppraisal || 0,
        ocrData: ocrData || {},
        prequalId: application?.id || prequalData?.id
      };
      
      console.log("Generating PDF with data:", pdfData);
      
      // Generate PDF blob
      const blob = await generatePrequalSummaryPDF(pdfData);
      
      // Create a download URL
      const url = URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `${pdfData.businessName.replace(/\s+/g, '_')}_Prequalification_Summary.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setIsGeneratingPDF(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setIsGeneratingPDF(false);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <main className="py-10">
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Header with back button */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Prequalification Details
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                View and manage prequalification information for {prequalData?.businessName || application?.businessName || 'this business'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onBack}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>
              
              {/* PDF Download Button */}
              <button
                onClick={handleGenerateSummaryPDF}
                disabled={isGeneratingPDF || !prequalData}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
              >
                {isGeneratingPDF ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    Download Summary
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Prequalification Amount Card */}
          <div className="bg-gradient-to-br from-white to-green-50 shadow-lg rounded-xl overflow-hidden mb-8 border border-green-100">
            <div className="px-6 py-8 sm:px-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold leading-7 text-gray-900 flex items-center">
                    <BuildingOfficeIcon className="h-6 w-6 text-indigo-600 mr-2" />
                    {prequalData?.businessName || application?.businessName || 'Business'} 
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Prequalification Status: <span className="font-medium text-indigo-600">Approved</span>
                  </p>
                </div>
                
                <div className="mt-4 sm:mt-0">
                  <div className="flex items-center justify-center sm:justify-end">
                    <div className="bg-white rounded-lg shadow-sm border border-green-200 px-6 py-4">
                      <p className="text-sm text-gray-500 mb-1">Prequalified Amount</p>
                      <p className="text-3xl font-bold text-green-600">
                        {formatCurrency(prequalData?.prequalifiedAmount || application?.prequalifiedAmount || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                  {prequalData?.prequalificationType || application?.prequalificationType || 'Standard Financing'}
                </span>
                {(prequalData?.prequalificationWithSalary || application?.prequalificationWithSalary) && (
                  <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-700/10">
                    With $75k Salary
                  </span>
                )}
                <span className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-sm font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                  SBA Loan
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Add Borrower Card */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-indigo-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-white">
                <div className="flex items-center">
                  <UserPlusIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Add Borrower</h3>
                </div>
                <button
                  onClick={() => setIsBorrowersExpanded(!isBorrowersExpanded)}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isBorrowersExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isBorrowersExpanded && (
                <div className="border-t border-indigo-100 px-6 py-5">
                  {/* Signup Link Section */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Share this link with potential borrowers
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <LinkIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          readOnly
                          value={signupLink}
                          className="block w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-600 focus:outline-none"
                        />
                      </div>
                      <button
                        onClick={copySignupLink}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm transition-all ${
                          linkCopied
                            ? 'bg-green-600 text-white'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700'
                        }`}
                      >
                        <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                        {linkCopied ? 'Copied!' : 'Copy Link'}
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      When borrowers sign up through this link, they'll automatically have this prequalified listing added to their account.
                    </p>
                  </div>

                  {/* Chosen Buyer Display */}
                  {chosenBuyer && (
                    <div className="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 mr-3">
                            <StarIcon className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-green-800">Chosen Buyer</p>
                            <p className="text-sm text-green-700">
                              {chosenBuyer.firstName} {chosenBuyer.lastName}
                            </p>
                            <p className="text-xs text-green-600">{chosenBuyer.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveChosenBuyer}
                          className="text-xs text-red-600 hover:text-red-800 underline"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Borrowers List */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Registered Borrowers ({borrowers.length})
                    </h4>
                    {borrowers.length > 0 ? (
                      <div className="space-y-3">
                        {borrowers.map((borrower, index) => {
                          // Only compare if both values exist and are not empty
                          const isChosenBuyer = chosenBuyer && (
                            (chosenBuyer.email && borrower.email && chosenBuyer.email === borrower.email) ||
                            (chosenBuyer.id && borrower.id && chosenBuyer.id === borrower.id) ||
                            (chosenBuyer.odeddiudi && borrower.odeddiudi && chosenBuyer.odeddiudi === borrower.odeddiudi)
                          );

                          return (
                            <div
                              key={borrower.id || index}
                              className={`flex items-center justify-between p-3 rounded-lg border ${
                                isChosenBuyer
                                  ? 'bg-green-50 border-green-300'
                                  : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-center">
                                <UserCircleIcon className={`h-8 w-8 mr-3 ${isChosenBuyer ? 'text-green-500' : 'text-gray-400'}`} />
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-gray-900">
                                      {borrower.firstName} {borrower.lastName}
                                    </p>
                                    {isChosenBuyer && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                                        Selected
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500">{borrower.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-400">
                                  {borrower.signedUpAt
                                    ? new Date(
                                        typeof borrower.signedUpAt === 'string'
                                          ? borrower.signedUpAt
                                          : borrower.signedUpAt.seconds
                                            ? borrower.signedUpAt.seconds * 1000
                                            : borrower.signedUpAt
                                      ).toLocaleDateString()
                                    : 'N/A'}
                                </span>
                                {!isChosenBuyer && (
                                  <button
                                    onClick={() => handleChooseBuyer(borrower)}
                                    disabled={isSelectingBuyer}
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    {isSelectingBuyer ? (
                                      <span>Selecting...</span>
                                    ) : (
                                      <>
                                        <StarIcon className="w-3 h-3 mr-1" />
                                        Choose as Buyer
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <UserPlusIcon className="mx-auto h-10 w-10 text-gray-300" />
                        <p className="mt-2 text-sm text-gray-500">No borrowers have signed up yet</p>
                        <p className="text-xs text-gray-400 mt-1">Share the link above to invite borrowers</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Business Details */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Business Details</h3>
                </div>
                <button
                  onClick={toggleBusinessDetails}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isBusinessDetailsExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isBusinessDetailsExpanded && (
                <div className="border-t border-gray-100 px-6 py-5">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Business name</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-medium">
                        {prequalData?.businessName || application?.businessName || 'N/A'}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Description</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData?.businessDescription || application?.businessDescription || 'No description provided.'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" />
                        Location
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData?.businessCity || application?.businessCity || 'N/A'}, 
                        {prequalData?.businessState || application?.businessState || 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-1" />
                        Purchase price
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 font-medium">
                        {formatCurrency(prequalData?.purchasePrice || application?.purchasePrice || 0)}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
              
            {/* Financial Overview */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <ChartBarIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Financial Overview</h3>
                </div>
                <button
                  onClick={toggleFinancialOverview}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isFinancialOverviewExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isFinancialOverviewExpanded && (
                <div className="border-t border-gray-100 px-6 py-5">
                  {Object.keys(ocrData).length > 0 ? (
                    <>
                      <div className="bg-gray-50 p-6 rounded-lg mb-6">
                        <PrequalFinancialMetricsDisplay 
                          ocrData={ocrData} 
                          loanData={prequalData || application || {}} 
                        />
                      </div>
                      
                      <div className="mt-6 overflow-x-auto">
                        <PrequalificationTable 
                          ocrData={ocrData} 
                          loanData={prequalData || application || {}} 
                        />
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <ChartBarIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500 italic">No financial data available for this prequalification.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Intake Documents - Only show for prequalified or buyer_selected status */}
            {(application?.status === 'prequalified' || application?.status === 'buyer_selected' ||
              prequalData?.status === 'prequalified' || prequalData?.status === 'buyer_selected') && (
              <BusinessIntakeDocuments
                prequalId={application?.id}
                brokerId={prequalData?.brokerId || application?.brokerId}
                businessName={prequalData?.businessName || application?.businessName}
              />
            )}

            {/* Uploaded Files */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <ClipboardDocumentListIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Uploaded Files</h3>
                </div>
                <button
                  onClick={toggleUploadedFiles}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isUploadedFilesExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isUploadedFilesExpanded && (
                <div className="border-t border-gray-100">
                  {uploadedFiles && uploadedFiles.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                      {uploadedFiles.map((file, index) => (
                        <div 
                          key={file.id || file.fileId || index} 
                          className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors border border-gray-200"
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-indigo-50 rounded-lg">
                              <DocumentIcon className="h-5 w-5 text-indigo-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.fileName || file.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {file.label || file.documentName || 'Document'}
                              </p>
                              <div className="mt-2">
                                <a
                                  href={file.fileUrl || file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                                >
                                  View Document
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-6 py-8 text-center">
                      <DocumentIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500 italic">No files uploaded yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default PrequalificationDetailsView;